import { makeAutoObservable } from 'mobx'
import {
  type IColumn,
  type IRowBase,
  type IRowExtractType,
  type TableStore,
} from 'shared/ui/TableNew'
import { TableColumnsCacheStore } from 'shared/ui/TableNew/store/tableColumnsCacheStore'

export class TableColumnsStore<TRow extends IRowBase = IRowBase> {
  private _columnsMap: Map<string, IColumn<TRow>> = new Map()
  private _columnsChildMap: Map<string, IColumn<IRowExtractType<TRow>>> = new Map()
  private _visibleColumnsIds: string[] = []
  private _pinnedColumnIds: string[] = []
  private _tableColumnsCacheStore: TableColumnsCacheStore<TRow>

  constructor(private _tableStore: TableStore<TRow>) {
    this._tableColumnsCacheStore = new TableColumnsCacheStore(this)

    makeAutoObservable(this)
  }

  get config() {
    return this._tableStore.config
  }

  get columns() {
    return Array.from(this._columnsMap.values())
  }

  get columnsChild() {
    return Array.from(this._columnsChildMap.values())
  }

  get columnsIds() {
    return this.columns.map((column) => column.field)
  }

  get visibleColumnsIds() {
    if (this._visibleColumnsIds.length) return this._visibleColumnsIds

    return this.columnsIds
  }

  get pinnedColumnsIds() {
    return this.visibleColumnsIds.filter((key) => this._pinnedColumnIds.includes(key))
  }

  get unpinnedColumnsIds() {
    return this.visibleColumnsIds.filter((key) => !this._pinnedColumnIds.includes(key))
  }

  get useColumnsCache() {
    return Boolean(this.config?.useColumnsCache || this.config?.saveColumns)
  }

  getPinnedColumns = <T>(columns: IColumn<T>[]) => {
    return columns.reduce<IColumn<T>[]>((state, item) => {
      if (this.pinnedColumnsIds.includes(item.field)) state.push(item)

      return state
    }, [])
  }

  getUnpinnedColumns = <T>(columns: IColumn<T>[]) => {
    return columns.reduce<IColumn<T>[]>((state, item) => {
      if (this.unpinnedColumnsIds.includes(item.field)) state.push(item)

      return state
    }, [])
  }

  getColumn = (key: string) => {
    return this._columnsMap.get(key)
  }

  getColumnChild = (key: string) => {
    return this._columnsChildMap.get(key)
  }

  setColumns = (columns: IColumn<TRow>[]) => {
    this._columnsMap.clear()
    columns.forEach((column) => {
      if (column.isHideByDefault) return

      this._columnsMap.set(column.field, column)
    })

    this._tableStore.rows.forEach((row) => {
      row.setColumnsKeys(this.visibleColumnsIds)
    })
  }

  setColumnsChild = (columns?: IColumn<IRowExtractType<TRow>>[]) => {
    if (!columns) return

    this._columnsChildMap.clear()
    columns.forEach((column) => {
      this._columnsChildMap.set(column.field, column)
    })
  }

  setPinnedColumnIds = (ids: string[]) => {
    this._pinnedColumnIds = ids
  }

  initVisibleColumnsIds = async () => {
    if (!this.config.useColumnsCache) {
      this._tableColumnsCacheStore.deleteCache()

      return
    }

    if (this.config.getColumns) {
      const data = await this.config.getColumns

      this._visibleColumnsIds = data || []
    } else if (this.config.useColumnsCache) {
      this._visibleColumnsIds = this._tableColumnsCacheStore.getCache()
    }
  }

  setVisibleColumnsIds = (ids: string[]) => {
    if (this.config.saveColumns) {
      this.config.saveColumns(ids)
    } else if (this.config.useColumnsCache) {
      this._tableColumnsCacheStore.addCache(ids)
    }

    this._visibleColumnsIds = ids
  }
}
